import "react-quill-new/dist/quill.snow.css";
import ReactQuill from "react-quill-new";
import {
    quillFormats,
    quillModules,
    QUILL_TABLE_TOOLBAR_EVENT,
    QuillTableModule,
    QuillTableToolbarEventDetail,
} from "../../../tools/QuillConfig";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import CustomError from "../../../custom-components/form-inputs/CustomError";
import TablePopover from "./quill-table/TablePopover";
import InsertTableGrid from "./quill-table/InsertTableGrid";
import TableActions from "./quill-table/TableActions";

interface QuillInputProps {
    value: string;
    onChange: (value: string) => void;
    errorKey: string;
    errorLabel: string;
    placeholder: string;
}

const QuillInput: FC<QuillInputProps> = ({ value, onChange, errorKey, errorLabel, placeholder }) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [tablePopover, setTablePopover] = useState<QuillTableToolbarEventDetail | null>(null);

    // The Quill toolbar handler (see QuillConfig) dispatches this event instead of
    // implementing the table UI itself; React owns the popover state and behavior.
    useEffect(() => {
        const wrapperEl = wrapperRef.current;
        if (!wrapperEl) return;

        const handleTableToolbarClick = (event: Event) => {
            setTablePopover((event as CustomEvent<QuillTableToolbarEventDetail>).detail);
        };

        wrapperEl.addEventListener(QUILL_TABLE_TOOLBAR_EVENT, handleTableToolbarClick);
        return () => wrapperEl.removeEventListener(QUILL_TABLE_TOOLBAR_EVENT, handleTableToolbarClick);
    }, []);

    const closeTablePopover = useCallback(() => setTablePopover(null), []);

    const runTableAction = useCallback(
        (action: (tableModule: QuillTableModule) => void) => {
            if (!tablePopover) return;
            const { quill, tableModule, savedRange } = tablePopover;

            // Restore the selection captured when the toolbar button was clicked, since
            // interacting with the popover (a real DOM element) can move it away.
            if (savedRange) {
                quill.setSelection(savedRange, "silent");
            }
            action(tableModule);
            closeTablePopover();
        },
        [tablePopover, closeTablePopover]
    );

    return (
        <>
            <CustomError errorKey={errorKey} name={errorLabel} />
            <div ref={wrapperRef}>
                <ReactQuill
                    modules={quillModules}
                    formats={quillFormats}
                    theme="snow"
                    className="flex flex-col min-h-[375px] resize-none"
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                />
                {tablePopover && (
                    <TablePopover anchorRect={tablePopover.anchorRect} onClose={closeTablePopover}>
                        {tablePopover.isInsideTable ? (
                            <TableActions
                                onInsertRowAbove={() => runTableAction((tableModule) => tableModule.insertRowAbove())}
                                onInsertRowBelow={() => runTableAction((tableModule) => tableModule.insertRowBelow())}
                                onInsertColumnLeft={() =>
                                    runTableAction((tableModule) => tableModule.insertColumnLeft())
                                }
                                onInsertColumnRight={() =>
                                    runTableAction((tableModule) => tableModule.insertColumnRight())
                                }
                                onDeleteRow={() => runTableAction((tableModule) => tableModule.deleteRow())}
                                onDeleteColumn={() => runTableAction((tableModule) => tableModule.deleteColumn())}
                                onDeleteTable={() => runTableAction((tableModule) => tableModule.deleteTable())}
                            />
                        ) : (
                            <InsertTableGrid
                                onInsert={(rows, columns) =>
                                    runTableAction((tableModule) => tableModule.insertTable(rows, columns))
                                }
                            />
                        )}
                    </TablePopover>
                )}
            </div>
        </>
    );
};

export default QuillInput;
